import { useCallback } from 'react';
import {
  ReactFlow, Background, BackgroundVariant, MiniMap, Controls, useReactFlow,
  type OnNodesChange, type OnEdgesChange, type OnConnect, type NodeMouseHandler,
} from '@xyflow/react';
import { WorkflowNodeComponent } from './WorkflowNode';
import type { WorkflowNode, WorkflowEdge, WorkflowNodeKind } from './types';

// nodeTypes must be a stable object (defined at module scope) or React Flow
// re-mounts every node on each render.
const nodeTypes = { workflow: WorkflowNodeComponent };

interface FlowCanvasProps {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  onNodesChange: OnNodesChange<WorkflowNode>;
  onEdgesChange: OnEdgesChange<WorkflowEdge>;
  onConnect: OnConnect;
  onNodeClick: NodeMouseHandler;
  /** Click on empty canvas (pane) — used to deselect the active node. */
  onPaneClick?: () => void;
  onDropKind: (kind: WorkflowNodeKind, position: { x: number; y: number }) => void;
}

export function FlowCanvas({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onNodeClick, onPaneClick, onDropKind }: FlowCanvasProps) {
  const { screenToFlowPosition } = useReactFlow();

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const kind = e.dataTransfer.getData('application/reactflow') as WorkflowNodeKind;
    if (!kind) return;
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    onDropKind(kind, position);
  }, [screenToFlowPosition, onDropKind]);

  return (
    <div className="h-full w-full relative overflow-hidden" style={{ background: 'var(--ink-faint)' }} data-canvas>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        onDrop={onDrop}
        onDragOver={onDragOver}
        fitView
        fitViewOptions={{ padding: 0.2 }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} className="opacity-60" />
        <MiniMap pannable zoomable className="!bg-white" />
        <Controls showInteractive={false} />
      </ReactFlow>

      <div className="absolute bottom-3 left-3 bg-slate-900/90 text-slate-300 border border-slate-800 px-2.5 py-1 rounded-md text-[9px] font-mono shadow-xs pointer-events-none z-10">
        Drag nodes from the palette · drag a node's right handle to connect · scroll to zoom.
      </div>
    </div>
  );
}
